import React, { useState } from 'react';
import './Sidebar.css';

const EXCELLENCE_WEEKS = Array.from({ length: 30 }, (_, i) => i + 1);
const SELF_GUIDED_MODULES = [
  { slug: 'DM', label: 'Decision Making' },
  { slug: 'VR', label: 'Verbal Reasoning' },
  { slug: 'QR', label: 'Quantitative Reasoning' },
  { slug: 'SJT', label: 'Situational Judgement' },
  { slug: 'exam_tips', label: 'Exam Tips' },
];
const QUESTION_BANK_SECTIONS = [
  { slug: 'DM', label: 'Decision Making' },
  { slug: 'VR', label: 'Verbal Reasoning' },
  { slug: 'QR', label: 'Quantitative Reasoning' },
  { slug: 'SJT', label: 'Situational Judgement' },
];

// Minimal single-color line icons (Feather/Lucide-style, 20x20, stroke-based)
// instead of emoji — emoji render inconsistently across platforms and read
// as informal; a consistent icon set is a big part of what makes a sidebar
// look like a real product rather than a text list.
const ICONS = {
  home: 'M3 10.5 12 3l9 7.5M5 9.5V21h5v-6h4v6h5V9.5',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 11v6M12 7.5v.01',
  graduation: 'M2 8.5 12 4l10 4.5-10 4.5-10-4.5Zm4 2.3V16c0 1.5 2.7 3 6 3s6-1.5 6-3v-5.2M20 9v6.5',
  book: 'M4 4.5A2.5 2.5 0 0 1 6.5 2H20v17.5A2.5 2.5 0 0 1 17.5 22H6.5A2.5 2.5 0 0 1 4 19.5v-15ZM4 17h14',
  clipboard: 'M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1ZM6 6h12v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6Zm3 6h6M9 15h6',
  database: 'M12 5c4.4 0 8-1.1 8-2.5S16.4 0 12 0 4 1.1 4 2.5 7.6 5 12 5Zm8-2.5V19c0 1.4-3.6 2.5-8 2.5S4 20.4 4 19V2.5M4 8.5c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5M4 14c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5',
  chart: 'M4 20V10m6 10V4m6 16v-7m6 7H2',
  results: 'M6 3h9l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm2 8 3 3 5-5',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-1.6-7.3c0-1.9 2.2-1.7 2.2-3.4 0-.9-.7-1.5-1.7-1.5-.8 0-1.4.4-1.7 1M12 17v.01',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7-1a2.75 2.75 0 1 0 0-5.5M2.5 20c0-3.3 2.9-6 6.5-6s6.5 2.7 6.5 6M16.5 14.3c2.7.4 4.5 2.4 4.5 5.2',
};

function Icon({ path }) {
  return (
    <svg className="sidebar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

function Chevron({ open }) {
  return (
    <svg className={`sidebar-chevron ${open ? 'sidebar-chevron-open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

// Groups (Excellence Program / Self-Guided Study / Practice Exams / Question
// Bank) expand in place as nested sidebar sub-items, matching the layout the
// user specifically asked for ("split into 30 tabs (side tabs)"), rather
// than a separate list page. Question Bank sub-items break straight out to
// the existing full-page PracticeMode component (there's no in-shell list
// step, since PracticeMode's own topic picker already serves that role);
// Practice Exams sub-items instead land on an in-shell paper-list panel,
// since picking a specific paper is a separate step from picking mini/full.
function Sidebar({ user, nav, setNav, onStartPractice }) {
  const [expanded, setExpanded] = useState({});

  const toggleGroup = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const go = (section, sub = null) => setNav({ section, sub });
  const isActive = (section, sub = null) => nav.section === section && nav.sub === sub;

  return (
    <nav className="sidebar">
      <div className="sidebar-section-label">Study</div>

      <button className={`sidebar-item ${isActive('home') ? 'sidebar-item-active' : ''}`} onClick={() => go('home')}>
        <Icon path={ICONS.home} /> Home
      </button>
      <button className={`sidebar-item ${isActive('about-ucat') ? 'sidebar-item-active' : ''}`} onClick={() => go('about-ucat')}>
        <Icon path={ICONS.info} /> What is UCAT?
      </button>

      <div className="sidebar-group">
        <button
          className={`sidebar-group-header ${isActive('excellence') ? 'sidebar-item-active' : ''}`}
          onClick={() => toggleGroup('excellence')}
        >
          <Icon path={ICONS.graduation} />
          <span className="sidebar-label">UCAT Excellence Program</span>
          <Chevron open={!!expanded.excellence} />
        </button>
        {expanded.excellence && (
          <div className="sidebar-subitems">
            {EXCELLENCE_WEEKS.map((w) => (
              <button
                key={w}
                className={`sidebar-subitem ${isActive('excellence', w) ? 'sidebar-item-active' : ''}`}
                onClick={() => go('excellence', w)}
              >
                Week {w}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sidebar-group">
        <button
          className={`sidebar-group-header ${isActive('self-guided') ? 'sidebar-item-active' : ''}`}
          onClick={() => toggleGroup('selfGuided')}
        >
          <Icon path={ICONS.book} />
          <span className="sidebar-label">Self-Guided Study</span>
          <Chevron open={!!expanded.selfGuided} />
        </button>
        {expanded.selfGuided && (
          <div className="sidebar-subitems">
            {SELF_GUIDED_MODULES.map((m) => (
              <button
                key={m.slug}
                className={`sidebar-subitem ${isActive('self-guided', m.slug) ? 'sidebar-item-active' : ''}`}
                onClick={() => go('self-guided', m.slug)}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sidebar-group">
        <button
          className={`sidebar-group-header ${isActive('practice-exams') ? 'sidebar-item-active' : ''}`}
          onClick={() => toggleGroup('practiceExams')}
        >
          <Icon path={ICONS.clipboard} />
          <span className="sidebar-label">Practice Exams</span>
          <Chevron open={!!expanded.practiceExams} />
        </button>
        {expanded.practiceExams && (
          <div className="sidebar-subitems">
            <button
              className={`sidebar-subitem ${isActive('practice-exams', 'mini') ? 'sidebar-item-active' : ''}`}
              onClick={() => go('practice-exams', 'mini')}
            >
              Mini Mocks
            </button>
            <button
              className={`sidebar-subitem ${isActive('practice-exams', 'full') ? 'sidebar-item-active' : ''}`}
              onClick={() => go('practice-exams', 'full')}
            >
              Full Mocks
            </button>
          </div>
        )}
      </div>

      <div className="sidebar-group">
        <button
          className={`sidebar-group-header ${isActive('question-bank') ? 'sidebar-item-active' : ''}`}
          onClick={() => { toggleGroup('questionBank'); go('question-bank'); }}
        >
          <Icon path={ICONS.database} />
          <span className="sidebar-label">Question Bank</span>
          <Chevron open={!!expanded.questionBank} />
        </button>
        {expanded.questionBank && (
          <div className="sidebar-subitems">
            {QUESTION_BANK_SECTIONS.map((s) => (
              <button key={s.slug} className="sidebar-subitem" onClick={() => onStartPractice(s.slug)}>
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sidebar-section-label">Progress</div>

      <button className={`sidebar-item ${isActive('my-results') ? 'sidebar-item-active' : ''}`} onClick={() => go('my-results')}>
        <Icon path={ICONS.results} /> My Results
      </button>
      <button className={`sidebar-item ${isActive('help') ? 'sidebar-item-active' : ''}`} onClick={() => go('help')}>
        <Icon path={ICONS.help} /> Help
      </button>

      {user.role === 'admin' && (
        <>
          <div className="sidebar-section-label">Admin</div>
          <button
            className={`sidebar-item ${isActive('user-management') ? 'sidebar-item-active' : ''}`}
            onClick={() => go('user-management')}
          >
            <Icon path={ICONS.users} /> User Management
          </button>
        </>
      )}
    </nav>
  );
}

export default Sidebar;
